// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Loader2, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface SaveDiscardPaneProps {
  onDiscard: () => void;
  saving?: boolean;
  saveDisabled?: boolean;
  /** The form Save submits — needed when the pane renders outside it (e.g. docked in a `PageFooter`). */
  form?: string;
  /**
   * For a container that already provides the chrome (a `PageFooter`): no box, an "Unsaved changes"
   * note on the left — away from the form, the bar has to say why it appeared — and buttons on the right.
   */
  docked?: boolean;
  /** Docked only: how many fields changed — the note reads "5 unsaved changes" instead of "Unsaved changes". */
  changeCount?: number;
}

export function SaveDiscardPane({ onDiscard, saving, saveDisabled, form, docked, changeCount }: SaveDiscardPaneProps) {
  const { t } = useTranslation("back-office");

  return (
    <div className={cn("flex items-center gap-3", !docked && "rounded-lg border bg-muted/50 p-4")}>
      {docked && (
        <span className="mr-auto text-sm text-muted-foreground">
          {changeCount ? t("unsavedChanges.count", { count: changeCount }) : t("unsavedChanges.label")}
        </span>
      )}
      <Button type="button" variant="outline" onClick={onDiscard}>
        {t("buttons.discard")}
      </Button>
      <Button type="submit" form={form} disabled={saving || saveDisabled}>
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        {t("buttons.save")}
      </Button>
    </div>
  );
}
